import axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

import { env } from "@/lib/env";
import { storage } from "@/lib/storage";
import { errorStore } from "@/stores";
import { HttpError } from "@/types";

const guacBaseUrl = env.getGuacBaseUrl();
const httpClient = axios.create({
  baseURL: guacBaseUrl,
});

// Intercept every request and add authorization header to it.
httpClient.interceptors.request.use((config) => {
  // If the request is to login, do not add the authorization header.
  if (config.url === "auth/login") {
    return config;
  }

  const user = storage.getUser();
  if (user?.accessToken) {
    config.headers.Authorization = `Bearer ${user.accessToken}`;
  }
  return config;
});

const setErrorMessage = errorStore.getState().setErrorMessage;
httpClient.interceptors.response.use(
  (response) => response,
  (error: HttpError<{ message?: string }>) => {
    // 401 errors are handled by the auth interceptor below!
    if (error.response?.status !== 401) {
      setErrorMessage(error.response?.data.message ?? "Something went wrong");
    }
    return Promise.reject(error);
  },
);

// Use `axios-auth-refresh` to refresh the access token when it expires.
// https://github.com/Flyrell/axios-auth-refresh?tab=readme-ov-file#usage

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const refreshAuthLogic = async (failedRequest: any) => {
  try {
    const user = storage.getUser();
    if (!user) {
      throw new Error("User not found in local storage");
    }

    const url = `${guacBaseUrl}/auth/refresh`;
    const tokenRefreshResponse = await axios.post(url, {
      id: user.id,
      refreshToken: user.refreshToken,
    });
    storage.setUser({
      ...user,
      // eslint-disable-next-line
      accessToken: tokenRefreshResponse.data.accessToken,
      // eslint-disable-next-line
      refreshToken: tokenRefreshResponse.data.refreshToken,
    });
    // eslint-disable-next-line
    failedRequest.response.config.headers["Authorization"] =
      // eslint-disable-next-line
      "Bearer " + tokenRefreshResponse.data.token;
    // eslint-disable-next-line
    return Promise.resolve();
  } catch (error) {
    storage.clear();
    window.location.href = "/sign-in";
    // eslint-disable-next-line
    return Promise.reject(error);
  }
};
createAuthRefreshInterceptor(httpClient, refreshAuthLogic, {
  statusCodes: [401],
});

export { httpClient };
